import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import { useI18n } from '../i18n';
import { getSessionToken } from '../services/ltiSession';

/**
 * Fixed button shown only when SARAS is embedded inside an iframe (typical
 * Moodle/LMS launch with `Embed, without blocks`). Click reopens SARAS in a
 * fresh top-level tab and re-attaches the current session JWT in the URL so
 * the new tab's bootstrap (initializeLTISession) can pick it up.
 *
 * sessionStorage is per-tab, so we cannot rely on it to ferry the token to a
 * new tab — putting it in the URL once, on a user-initiated click, is the
 * accepted trade-off. The destination tab clears it from the URL on load.
 */
export default function OpenInNewTabBanner() {
    const { t } = useI18n();
    const [isEmbedded, setIsEmbedded] = useState(false);

    useEffect(() => {
        try {
            setIsEmbedded(window.self !== window.top);
        } catch (_) {
            // Cross-origin frame access throws — that itself confirms we're framed.
            setIsEmbedded(true);
        }
    }, []);

    if (!isEmbedded) return null;

    const handleOpen = () => {
        const token = getSessionToken();
        const base = `${window.location.origin}${window.location.pathname}`;
        const url = token
            ? `${base}?session=${encodeURIComponent(token)}`
            : window.location.href;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <button
            type="button"
            onClick={handleOpen}
            title={t('openInFullTabTitle')}
            className="fixed top-3 right-3 z-50 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-xs font-medium text-gray-700 shadow-md ring-1 ring-gray-200 hover:bg-white hover:shadow-lg transition"
        >
            <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
            <span>{t('openInFullTab')}</span>
        </button>
    );
}
